import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./Questionnaire.css";
import publicAxios from "../../utils/axios/publicAxios";

const Questionnaire = () => {
  const { slug } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // Fetch questionnaire by slug
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        const response = await publicAxios.get(`/public/questionnaire/${slug}`);
        setQuestionnaire(response.data.questionnaire);
      } catch (error) {
        console.error("Error fetching questionnaire:", error);
        setMessage("⚠️ Failed to load questionnaire. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionnaire();
  }, [slug]);

  // Handle answer changes
  const handleAnswerChange = (sectionIndex, questionIndex, value) => {
    setAnswers((prev) => ({
      ...prev,
      [`${sectionIndex}-${questionIndex}`]: value,
    }));
  };

  const handleCheckboxChange = (sectionIndex, questionIndex, option) => {
    const key = `${sectionIndex}-${questionIndex}`;
    setAnswers((prev) => {
      const existing = prev[key] || [];
      return {
        ...prev,
        [key]: existing.includes(option)
          ? existing.filter((item) => item !== option)
          : [...existing, option],
      };
    });
  };

  // Toggle section collapse/expand
  const toggleSection = (index) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Submit questionnaire answers
  const handleSubmit = async () => {
    if (!questionnaire) return;

    const formattedAnswers = questionnaire.sections.map((section, sIndex) => ({
      sectionTitle: section.title,
      questions: section.questions.map((q, qIndex) => ({
        question: q.text,
        answer: answers[`${sIndex}-${qIndex}`] || "",
      })),
    }));

    try {
      setLoading(true);
      setMessage("");
      const res = await publicAxios.post("/public/submit-questionnaire", {
        slug: questionnaire.slug,
        responses: formattedAnswers,
      });

      if (res.status === 201) {
        console.log("Submitted successfully");
      }

      if (res.data.success) {
        setMessage("✅ Thank you! Your responses have been submitted.");
        setAnswers({});
      } else {
        setMessage("⚠️ Failed to submit questionnaire. Please try again.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setMessage("❌ Error submitting questionnaire.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="questionnaire-loading">
        <p>Loading questionnaire...</p>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="questionnaire-error">
        <p>{message || "Questionnaire not found."}</p>
      </div>
    );
  }

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-header">
        <h1>{questionnaire.title}</h1>
        <p className="questionnaire-description">{questionnaire.description}</p>
      </div>

      {questionnaire.sections.map((section, sIndex) => {
        const collapsed = collapsedSections[sIndex];
        return (
          <div key={sIndex} className="questionnaire-section">
            <div
              className="section-header"
              onClick={() => toggleSection(sIndex)}
            >
              <h2>{section.title}</h2>
              <span className="toggle-icon">
                {collapsed ? "▶" : "▼"}
              </span>
            </div>

            {!collapsed && (
              <div className="section-content">
                {section.questions.map((q, qIndex) => (
                  <div key={qIndex} className="questionnaire-question">
                    <label>{q.text}</label>

                    {/* Text Input */}
                    {q.type === "text" && (
                      <input
                        type="text"
                        value={answers[`${sIndex}-${qIndex}`] || ""}
                        onChange={(e) =>
                          handleAnswerChange(sIndex, qIndex, e.target.value)
                        }
                      />
                    )}

                    {/* Textarea */}
                    {q.type === "textarea" && (
                      <textarea
                        rows={4}
                        value={answers[`${sIndex}-${qIndex}`] || ""}
                        onChange={(e) =>
                          handleAnswerChange(sIndex, qIndex, e.target.value)
                        }
                      ></textarea>
                    )}

                    {/* Radio buttons */}
                    {q.type === "radio" && (
                      <div className="options-container">
                        {q.options.map((opt, i) => (
                          <label key={i} className="option-item">
                            <input
                              type="radio"
                              name={`${sIndex}-${qIndex}`}
                              checked={answers[`${sIndex}-${qIndex}`] === opt}
                              onChange={() =>
                                handleAnswerChange(sIndex, qIndex, opt)
                              }
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Checkbox options */}
                    {q.type === "checkbox" && (
                      <div className="options-container">
                        {q.options.map((opt, i) => (
                          <label key={i} className="option-item">
                            <input
                              type="checkbox"
                              checked={
                                answers[`${sIndex}-${qIndex}`]?.includes(opt) ||
                                false
                              }
                              onChange={() =>
                                handleCheckboxChange(sIndex, qIndex, opt)
                              }
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Select dropdown */}
                    {q.type === "select" && (
                      <select
                        value={answers[`${sIndex}-${qIndex}`] || ""}
                        onChange={(e) =>
                          handleAnswerChange(sIndex, qIndex, e.target.value)
                        }
                      >
                        <option value="">Select an option</option>
                        {q.options.map((opt, i) => (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="submit-container">
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Questionnaire"}
        </button>
        {message && <p className="submit-message">{message}</p>}
      </div>
    </div>
  );
};

export default Questionnaire;